import type { ChargeBreakdown, MultiCurrencyPrice, Price } from './Price';
import type { DiscountDurationType } from './Campaign';

// ===== Response types ===== //

export type PlansListResponse = PlanResponse[];

export interface PlanResponse {
  code: string;
  family?: string;
  frequency: PlanFrequency;
  addons: string[];
  /** Full recurring price (before any promotional discount). */
  price: MultiCurrencyPrice;
  /** `price` minus the winning auto-applied (PUBLIC) discounts. Equals `price` when none apply. */
  effectivePrice?: MultiCurrencyPrice;
  /** All active campaigns for this plan, public and gated. Empty/omitted when none are active. */
  discounts?: PlanDiscount[];
  quota?: PlanQuota;
  tripType: TripType;
  commitment?: Commitment;
  group?: string;
  markets?: Market[];
  subscriptionFees?: SubscriptionFees[];
}

export interface PlanDiscount {
  campaignCode: string;
  eligibility: string[];
  duration: DiscountDuration;
  /** True for auto-applied PUBLIC discounts already folded into effectivePrice; false for gated ones. */
  includedInEffectivePrice: boolean;
  gross: Price[];
  net: Price[];
}

export interface DiscountDuration {
  type: DiscountDurationType;
  /** Number of billing periods the discount applies to. Only set for FIXED. */
  periods?: number;
}

export interface PlanQuota {
  limit: number;
  usage: string;
}

export const TripTypes = { OW: 'OW', RT: 'RT', ALL: 'ALL' } as const;
export type TripType = keyof typeof TripTypes;

export interface Commitment {
  amount: string;
  unit: PeriodUnit;
}

export interface Market {
  origin: string;
  destinations: MarketDestination[];
}

export interface MarketDestination {
  destination: string;
  tags: string[];
}

export interface SubscriptionFees {
  type: FeeType;
  period?: FeePeriod | null;
  gross: Price[];
  net: Price[];
  additionalCharges?: ChargeBreakdown[];
}

export type FeeType = 'SIGN_UP' | 'RENEWAL';

export interface FeePeriod {
  amount: number;
  unit: PeriodUnit;
}

export type PeriodUnit = 'DAYS' | 'MONTHS' | 'YEARS';

export type PlanFrequency = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'BIMESTRIAL' | 'QUARTERLY' | 'ANNUAL';

// ===== Query types ===== //

export interface PlanListQueryParams {
  origin?: string;
  destination?: string;
  // API supports the following filters but they are now used from the frontend
  currency?: string;
  frequency?: string;
  family?: string;
}